import { useState } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import {
  canStartProviderOnboarding,
  providerErrorDetail,
  providerMessage,
  providerStatusClass,
  providerStatusLabel,
} from "../../../../core/primitives/provider-status";
import { ProvidersSectionActions, ProvidersSectionState } from "./providers-section-model";

type ProviderDefinition = {
  key: string;
  name: string;
  description: string;
  kind: "COMMERCE" | "PAYMENT";
  // "unavailable" means the integration exists but cannot be started from here.
  connectMode: "stripe" | "unavailable";
  // Shown in place of a connect button when connectMode is "unavailable".
  unavailableNote?: string;
  // What stops working on disconnect, so the confirmation says something.
  disconnectConsequence: string;
};

const PROVIDERS: ProviderDefinition[] = [
  {
    key: "shopify",
    name: "Shopify",
    description: "Sync products, inventory, and commerce workflows.",
    kind: "COMMERCE",
    // The Shopify app is not through app review yet, so starting OAuth from here
    // only walks the merchant into a wall. A store already connected — via the
    // embedded admin, or before this was switched off — keeps working, and keeps
    // its controls below.
    connectMode: "unavailable",
    unavailableNote: "Shopify connections aren't available yet. We'll turn this on once the app is approved.",
    disconnectConsequence: "Product and inventory sync stops until you connect it again.",
  },
  {
    key: "stripe",
    name: "Stripe",
    description: "Connect a Stripe account to take card payments and get paid out.",
    kind: "PAYMENT",
    connectMode: "stripe",
    disconnectConsequence: "You stop taking card payments until you connect an account again.",
  },
];

function ProviderSkeleton() {
  return <div className="h-32 animate-pulse rounded-row bg-surface" />;
}

function formatChecked(at: Date | null | undefined): string | null {
  if (!at) return null;
  const then = at.getTime();
  if (Number.isNaN(then)) return null;
  const minutes = Math.round((Date.now() - then) / 60_000);
  if (minutes < 1) return "Checked just now";
  if (minutes < 60) return `Checked ${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Checked ${hours}h ago`;
  return `Checked ${at.toLocaleDateString()}`;
}

export function ProvidersSection() {
  const state = useSubtree(ProvidersSectionState);
  const actions = useSubtree(ProvidersSectionActions);
  const observer = useObserver();
  const connections = observer.watch(state.providers);
  const status = observer.watch(state.status);
  const connectingProvider = observer.watch(state.connectingKey);
  const refreshingKey = observer.watch(state.refreshingKey);
  const disconnectingKey = observer.watch(state.disconnectingKey);
  const dashboardBusy = observer.watch(state.dashboardBusy);
  const confirmingDisconnect = observer.watch(state.confirmingDisconnect);
  const actionError = observer.watch(state.actionError);
  const stripeTestMode = observer.watch(state.stripeTestMode);
  const attachAccountId = observer.watch(state.attachAccountId);
  const attaching = observer.watch(state.attaching);
  const [copiedRef, setCopiedRef] = useState<string | null>(null);

  const copyRef = async (ref: string) => {
    await navigator.clipboard.writeText(ref);
    setCopiedRef(ref);
    setTimeout(() => setCopiedRef(null), 2000);
  };

  const getConnection = (providerKey: string) =>
    connections.find((connection) => connection.providerKey === providerKey);

  if (status.kind === "idle" || status.kind === "loading") {
    return (
      <section className="panel p-6 sm:p-8">
        <div className="mb-5 h-6 w-32 animate-pulse rounded bg-surface-strong" />
        <div className="space-y-3">
          <ProviderSkeleton />
          <ProviderSkeleton />
        </div>
      </section>
    );
  }

  if (status.kind === "error") {
    return (
      <section className="note note-danger p-6 sm:p-8">
        <h2 className="text-base font-semibold text-danger">Providers unavailable</h2>
        <p className="mt-2 text-sm text-muted">{status.message || "Unable to load provider status."}</p>
        <button type="button" onClick={actions.retry} className="btn-destructive btn-sm mt-4">
          Retry
        </button>
      </section>
    );
  }

  return (
    <section className="panel p-6 sm:p-8">
      <div className="mb-5">
        <h2 className="text-base font-semibold text-white">Providers</h2>
        <p className="mt-1 text-sm text-muted">Connect commerce and payment tools when your Store is ready.</p>
      </div>

      <div className="divide-y divide-line">
        {PROVIDERS.map((provider) => {
          const connection = getConnection(provider.key);
          const isConnected = connection?.status === "ACTIVE";
          const isLive = !!connection && connection.status !== "DISCONNECTED";
          const isConnecting = connectingProvider === provider.key;
          const isRefreshing = refreshingKey === provider.key;
          const isDisconnecting = disconnectingKey === provider.key;
          const message = providerMessage(connection, provider.name);
          const detail = providerErrorDetail(connection);
          const checked = formatChecked(connection?.lastSyncedAt);
          const canOnboard =
            provider.connectMode === "stripe" && !isConnected && canStartProviderOnboarding(connection);
          const error = actionError?.providerKey === provider.key ? actionError.message : null;

          return (
            <div key={provider.key} className="py-5 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h3 className="text-sm font-semibold text-white">{provider.name}</h3>
                <span className={`badge ${providerStatusClass(connection)}`}>{providerStatusLabel(connection)}</span>
              </div>
              <p className="mt-1 text-sm text-muted">{provider.description}</p>

              {/* One place to look for what is wrong and what to do about it: the
                  state, the plain-language reason and the single next step,
                  together. Everything else in the row stays quieter than this. */}
              {message && (
                <div className={`note mt-3 ${connection?.status === "ERROR" ? "note-danger" : "note-warn"}`}>
                  <p className="text-sm leading-relaxed text-white">{message}</p>
                  {canOnboard && (
                    <button
                      type="button"
                      onClick={() => void actions.connect(provider.key)}
                      disabled={isConnecting}
                      className="btn-primary btn-sm mt-3"
                    >
                      {isConnecting ? "Opening Stripe…" : "Finish setup on Stripe"}
                    </button>
                  )}
                  {/* Kept for support, collapsed for everyone else: the provider's
                      own wording names API fields a merchant cannot act on. */}
                  {detail && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-xs text-faint transition-colors hover:text-muted">
                        Details from {provider.name}
                      </summary>
                      <p className="mt-1.5 break-words text-xs leading-relaxed text-faint">{detail}</p>
                    </details>
                  )}
                </div>
              )}

              {provider.connectMode === "unavailable" && !isLive && provider.unavailableNote && (
                <p className="mt-3 text-sm leading-relaxed text-faint">{provider.unavailableNote}</p>
              )}

              {canOnboard && !message && (
                <button
                  type="button"
                  onClick={() => void actions.connect(provider.key)}
                  disabled={isConnecting}
                  className="btn-primary btn-sm mt-4"
                >
                  {isConnecting ? "Opening Stripe…" : "Connect Stripe"}
                </button>
              )}

              {error && <p className="note note-danger mt-3 text-sm text-danger">{error}</p>}

              {/* Controls for a live connection are independent of whether a new
                  one can be started — a Shopify store connected elsewhere still
                  needs to be refreshable and disconnectable from here. */}
              {isLive &&
                (confirmingDisconnect === provider.key ? (
                  <div className="note note-danger mt-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-white">
                      Disconnect {provider.name}? {provider.disconnectConsequence}
                    </p>
                    <div className="flex shrink-0 gap-2">
                      <button
                        type="button"
                        onClick={actions.cancelDisconnect}
                        disabled={isDisconnecting}
                        className="btn-ghost"
                      >
                        Keep it
                      </button>
                      <button
                        type="button"
                        onClick={() => void actions.disconnect(provider.key)}
                        disabled={isDisconnecting}
                        className="btn-destructive btn-xs"
                      >
                        {isDisconnecting ? "Disconnecting…" : "Disconnect"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {provider.key === "stripe" && connection?.externalAccountRef && (
                      <button
                        type="button"
                        onClick={actions.openDashboard}
                        disabled={dashboardBusy}
                        className="btn-chip"
                      >
                        {dashboardBusy ? "Opening…" : "Open Stripe dashboard"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void actions.refresh(provider.key)}
                      disabled={isRefreshing}
                      className="btn-chip"
                    >
                      {isRefreshing ? "Checking…" : "Check status"}
                    </button>
                    {checked && <span className="text-xs text-faint">{checked}</span>}
                    <button
                      type="button"
                      onClick={() => actions.startDisconnect(provider.key)}
                      className="btn-ghost ml-auto"
                    >
                      Disconnect
                    </button>
                  </div>
                ))}

              {/* An account id is support's business, not the merchant's, so it
                  sits last and quietest rather than under the heading. */}
              {isLive && connection?.externalAccountRef && (
                <div className="mt-2 flex items-center gap-2">
                  <p className="break-all font-mono text-xs text-faint">{connection.externalAccountRef}</p>
                  <button
                    type="button"
                    onClick={() => void copyRef(connection.externalAccountRef ?? "")}
                    className="btn-ghost text-xs"
                  >
                    {copiedRef === connection.externalAccountRef ? "Copied" : "Copy"}
                  </button>
                </div>
              )}

              {provider.key === "stripe" && stripeTestMode && (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    void actions.attachAccount();
                  }}
                  className="mt-4 border-t border-line pt-4"
                >
                  <label htmlFor="stripe-attach-account" className="text-[13px] font-medium text-white">
                    Use an existing test account
                  </label>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input
                      id="stripe-attach-account"
                      type="text"
                      autoComplete="off"
                      spellCheck={false}
                      value={attachAccountId}
                      onChange={(event) => actions.setAttachAccountId(event.target.value)}
                      placeholder="acct_…"
                      className="field min-w-56 flex-1 font-mono"
                    />
                    <button
                      type="submit"
                      disabled={attaching || !attachAccountId.trim()}
                      className="btn-primary btn-sm"
                    >
                      {attaching ? "Attaching…" : "Attach"}
                    </button>
                  </div>
                  <p className="mt-2 text-xs leading-snug text-muted">
                    Test mode only. Reuses a sandbox account already connected to this platform instead of creating a
                    new one.
                  </p>
                </form>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
